import { userHasFeature } from 'refactor/hooks/useUserHasFeature'

export const ORDINAL_CHAT_FEATURE = 'ordinals_chat'
export const ORDINAL_CHAT_REPLY_KEY = 'ordinal_chat_reply'

export function ordinalChatEnabled(): boolean {
    return userHasFeature(ORDINAL_CHAT_FEATURE)
}

type OrdinalChatAnswer = {
    id: string | number
    user_id?: string | number | null
    user?: { id?: string | number | null } | null
}

type OrdinalChatDelta = {
    ops?: Array<{ insert?: unknown }>
}

export type OrdinalChatReplyReference = {
    answerId: string
    runId: string
    name: string
    text: string
    thumbnailUrl?: string
}

export function getOrdinalChatReply(
    delta: OrdinalChatDelta | null | undefined,
): OrdinalChatReplyReference | null {
    for (const op of delta?.ops || []) {
        if (!op.insert || typeof op.insert !== 'object') continue
        const reply = (op.insert as Record<string, unknown>)[ORDINAL_CHAT_REPLY_KEY]
        if (!reply || typeof reply !== 'object') continue
        const reference = reply as Partial<OrdinalChatReplyReference>
        if (!reference.answerId || !reference.runId || !reference.name) continue
        return {
            answerId: String(reference.answerId),
            runId: String(reference.runId),
            name: String(reference.name),
            text: String(reference.text || ''),
            ...(reference.thumbnailUrl ? { thumbnailUrl: String(reference.thumbnailUrl) } : {}),
        }
    }
    return null
}

export function withoutOrdinalChatReply<T extends OrdinalChatDelta>(
    delta: T | null | undefined,
): T | OrdinalChatDelta {
    const source = delta || { ops: [] }
    return {
        ...source,
        ops: (source.ops || []).filter(op => {
            if (!op.insert || typeof op.insert !== 'object') return true
            return !(ORDINAL_CHAT_REPLY_KEY in (op.insert as Record<string, unknown>))
        }),
    } as T | OrdinalChatDelta
}

export function getOrdinalChatBodyDelta(
    delta: OrdinalChatDelta | null | undefined,
    fallbackText = '',
): OrdinalChatDelta {
    const fallback = fallbackText
        ? { ops: [{ insert: fallbackText }, { insert: '\n' }] }
        : { ops: [] }
    return withoutOrdinalChatReply(delta || fallback)
}

export function withOrdinalChatReply<T extends OrdinalChatDelta>(
    delta: T,
    reply: OrdinalChatReplyReference | null,
): T {
    const next = withoutOrdinalChatReply(delta) as T
    if (!reply) return next
    return {
        ...next,
        ops: [{ insert: { [ORDINAL_CHAT_REPLY_KEY]: reply } }, ...(next.ops || [])],
    } as T
}

export function appendOrdinalChatDraftText<T extends OrdinalChatDelta>(
    delta: T | null | undefined,
    text: string,
): T | OrdinalChatDelta {
    const appendedText = text.trim()
    if (!appendedText) return delta || { ops: [] }

    const existingOps = Array.isArray(delta?.ops) ? delta.ops : []
    if (existingOps.length === 0) {
        return { ...(delta || {}), ops: [{ insert: appendedText }, { insert: '\n' }] }
    }

    const existingText = existingOps
        .map(op => (typeof op.insert === 'string' ? op.insert : ''))
        .join('')
        .replace(/\n$/, '')
    const separator = existingText && !/\s$/.test(existingText) ? ' ' : ''
    const nextOps = existingOps.map(op => ({ ...op }))
    const lastOp = nextOps[nextOps.length - 1]
    const lastInsert = typeof lastOp?.insert === 'string' ? lastOp.insert : ''

    if (lastInsert.endsWith('\n')) {
        if (lastInsert === '\n') {
            nextOps.splice(nextOps.length - 1, 0, { insert: `${separator}${appendedText}` })
        } else {
            nextOps.splice(
                nextOps.length - 1,
                1,
                { ...lastOp, insert: lastInsert.slice(0, -1) },
                { insert: `${separator}${appendedText}` },
                { ...lastOp, insert: '\n' },
            )
        }
    } else {
        nextOps.push({ insert: `${separator}${appendedText}` }, { insert: '\n' })
    }

    return { ...(delta || {}), ops: nextOps }
}

export type OrdinalChatRun<T extends OrdinalChatAnswer> = {
    id: string
    userId: string
    answers: T[]
}

export function groupOrdinalChatAnswers<T extends OrdinalChatAnswer>(
    answers: T[],
): OrdinalChatRun<T>[] {
    return answers.reduce<OrdinalChatRun<T>[]>((runs, answer) => {
        const userId = String(answer.user_id ?? answer.user?.id ?? '')
        const previousRun = runs[runs.length - 1]

        if (previousRun?.userId === userId) {
            previousRun.answers.push(answer)
            return runs
        }

        runs.push({
            id: `${userId}:${String(answer.id)}`,
            userId,
            answers: [answer],
        })
        return runs
    }, [])
}

type OrdinalChatMediaStateInput = {
    sheetHeight: number
    headerHeight: number
    composerHeight: number
    threadContentHeight: number
    wasDocked: boolean
    desktop: boolean
}

export type OrdinalChatMediaState = {
    mediaHeight: number
    docked: boolean
}

type OrdinalChatRect = {
    top: number
    right: number
    bottom: number
    left: number
}

type OrdinalChatPopoverPositionInput = {
    boundary: OrdinalChatRect
    target: OrdinalChatRect
    anchorRight: number
    popoverWidth: number
    popoverHeight: number
    gap?: number
    margin?: number
}

export type OrdinalChatPopoverPosition = {
    left: number
    top: number
    placement: 'above' | 'below'
    maxHeight?: number
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export function resolveOrdinalChatPopoverPosition({
    boundary,
    target,
    anchorRight,
    popoverWidth,
    popoverHeight,
    gap = 10,
    margin = 10,
}: OrdinalChatPopoverPositionInput): OrdinalChatPopoverPosition {
    const minLeft = boundary.left + margin
    const maxLeft = Math.max(minLeft, boundary.right - margin - popoverWidth)
    const left = Math.round(clamp(anchorRight - popoverWidth, minLeft, maxLeft))
    const aboveTop = target.top - gap - popoverHeight
    const belowTop = target.bottom + gap
    const minTop = boundary.top + margin
    const maxTop = Math.max(minTop, boundary.bottom - margin - popoverHeight)
    const fitsAbove = aboveTop >= minTop
    const fitsBelow = belowTop <= maxTop
    const spaceAbove = Math.max(0, target.top - gap - minTop)
    const spaceBelow = Math.max(0, boundary.bottom - margin - target.bottom - gap)
    const placement = fitsAbove ? 'above' : fitsBelow || spaceBelow > spaceAbove ? 'below' : 'above'
    const availableHeight = placement === 'above' ? spaceAbove : spaceBelow
    const constrainedHeight = Math.min(popoverHeight, availableHeight)

    return {
        left,
        top: Math.round(placement === 'above' ? target.top - gap - constrainedHeight : belowTop),
        placement,
        ...(constrainedHeight < popoverHeight
            ? { maxHeight: Math.max(0, Math.floor(constrainedHeight)) }
            : {}),
    }
}

export function resolveOrdinalChatMediaState({
    sheetHeight,
    headerHeight,
    composerHeight,
    threadContentHeight,
    wasDocked,
    desktop,
}: OrdinalChatMediaStateInput): OrdinalChatMediaState {
    const freeHeight = sheetHeight - headerHeight - composerHeight - threadContentHeight
    const ceiling = sheetHeight * (desktop ? 0.5 : 0.55)
    const mediaHeight = Math.round(clamp(freeHeight - 24, 0, ceiling))
    const docked = wasDocked ? mediaHeight <= 150 : mediaHeight < 120

    return { mediaHeight, docked }
}
